import React, { useEffect, useState, useContext } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import {
    ArrowBack,
    CalendarMonth,
    ContentCopy,
    Email,
    OpenInNew,
    Person,
    Receipt,
    WhatsApp,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { api } from '../../../services/Main/Api'
import Masks from '../../../components/Shared/Masks'
import Context from '../../../contexts/Context'

const STATUS_MAP = {
    open: { label: 'Em Aberto', color: '#3B82F6' },
    pending: { label: 'Pendente', color: '#F59E0B' },
    processing: { label: 'Processando', color: '#8B5CF6' },
    paid: { label: 'Pago', color: '#22C55E' },
    overdue: { label: 'Atrasado', color: '#EF4444' },
    deleted: { label: 'Deletado', color: '#94A3B8' },
    canceling: { label: 'Cancelando', color: '#94A3B8' },
}

const getStatusChip = (statusKey) => {
    const found = STATUS_MAP[statusKey] || { label: statusKey, color: '#94A3B8' }
    return (
        <Chip
            label={found.label}
            size="small"
            sx={{
                bgcolor: `${found.color}14`,
                color: found.color,
                fontWeight: 600,
                fontSize: '0.8rem',
                height: 28,
                border: `1px solid ${found.color}30`,
            }}
        />
    )
}

const InfoRow = ({ label, value, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {icon && <Box sx={{ color: '#94A3B8', display: 'flex' }}>{icon}</Box>}
        <Typography variant="body2" sx={{ color: '#94A3B8', minWidth: 120 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
            {value || '—'}
        </Typography>
    </Box>
)

const ReceivablesDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const { user } = useContext(Context)

    const [invoice, setInvoice] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        setLoading(true)
        api.get(`receivables/${id}/`)
            .then((res) => setInvoice(res.data))
            .catch((err) => {
                console.error(err)
                enqueueSnackbar('Erro ao carregar recebimento.', { variant: 'error' })
            })
            .finally(() => setLoading(false))
    }, [id])

    const statusKey = invoice?.status?.key
    const fees = invoice?.fees || []
    const totalValue = invoice?.total || 0
    const totalPaid = invoice?.total_paid || 0
    const totalInterest = invoice?.total_interest_and_fine || 0
    const totalDiscount = invoice?.total_discount_value || 0

    const copyPaymentLink = () => {
        if (invoice?.payment_page) {
            navigator.clipboard.writeText(invoice.payment_page)
            enqueueSnackbar('Link copiado!', { variant: 'success' })
        }
    }

    return (
        <>
            <Helmet>
                <title>{invoice?.number ? `${invoice.number} - Recebimento` : 'Recebimento'} - Bloco B3</title>
            </Helmet>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                        <IconButton onClick={() => navigate('/financeiro/recebimentos')}>
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                    {loading ? <Skeleton width={200} /> : `Recebimento ${invoice?.number || ''}`}
                                </Typography>
                                {!loading && statusKey && getStatusChip(statusKey)}
                            </Box>
                        </Box>
                    </Box>

                    {loading ? (
                        <Skeleton variant="rounded" height={500} animation="pulse" />
                    ) : invoice ? (
                        <Grid container spacing={2}>
                            {/* Left column - Invoice info */}
                            <Grid item xs={12} md={8}>
                                {/* Invoice Info Card */}
                                <Card
                                    sx={{
                                        border: '1px solid #E2E8F0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        borderRadius: 2,
                                        mb: 2,
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#94A3B8',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                fontSize: '0.7rem',
                                                letterSpacing: '0.05em',
                                                mb: 2,
                                            }}
                                        >
                                            Informações do Recebimento
                                        </Typography>

                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <InfoRow
                                                    icon={<Person sx={{ fontSize: 18 }} />}
                                                    label="Cliente"
                                                    value={invoice.name}
                                                />
                                                <InfoRow
                                                    label="CPF/CNPJ"
                                                    value={invoice.cpf}
                                                />
                                                <InfoRow
                                                    icon={<Receipt sx={{ fontSize: 18 }} />}
                                                    label="Número"
                                                    value={invoice.number}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <InfoRow
                                                    icon={<CalendarMonth sx={{ fontSize: 18 }} />}
                                                    label="Vencimento"
                                                    value={invoice.due_date ? moment(invoice.due_date).format('DD/MM/YYYY') : null}
                                                />
                                                <InfoRow
                                                    label="Referência"
                                                    value={invoice.payment_month || invoice.reference_month}
                                                />
                                                <InfoRow
                                                    label="Criado em"
                                                    value={invoice.created_at ? moment(invoice.created_at).format('DD/MM/YYYY HH:mm') : null}
                                                />
                                            </Grid>
                                        </Grid>

                                        {invoice.paid_at && (
                                            <>
                                                <Divider sx={{ my: 2 }} />
                                                <InfoRow
                                                    label="Pago em"
                                                    value={moment(invoice.paid_at).format('DD/MM/YYYY HH:mm')}
                                                />
                                                {invoice.paid_by && (
                                                    <InfoRow
                                                        label="Forma de pagamento"
                                                        value={invoice.paid_by}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Fees Table Card */}
                                <Card
                                    sx={{
                                        border: '1px solid #E2E8F0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#94A3B8',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                fontSize: '0.7rem',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            Itens do Recebimento
                                        </Typography>
                                    </Box>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                                    <TableCell
                                                        sx={{
                                                            color: '#94A3B8',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            borderBottom: '1px solid #E2E8F0',
                                                        }}
                                                    >
                                                        Descrição
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            color: '#94A3B8',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            borderBottom: '1px solid #E2E8F0',
                                                        }}
                                                    >
                                                        Categoria
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            color: '#94A3B8',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            borderBottom: '1px solid #E2E8F0',
                                                        }}
                                                    >
                                                        Valor
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {fees.length > 0 ? (
                                                    fees.map((fee, index) => (
                                                        <TableRow
                                                            key={fee.id || index}
                                                            sx={{
                                                                '& td': { borderBottom: '1px solid #F1F5F9' },
                                                            }}
                                                        >
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                                    {fee.description}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ color: '#64748B' }}>
                                                                    {fee.category?.value || '—'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 600 }}>
                                                                    {Masks.money(String(fee.total || 0))}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: '#94A3B8' }}>
                                                            Nenhum item encontrado
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                                {/* Totals section */}
                                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                                    <TableCell colSpan={2}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                            Subtotal
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                            {Masks.money(String(totalValue))}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>

                                                {totalInterest > 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={2}>
                                                            <Typography variant="body2" sx={{ color: '#EF4444', fontWeight: 500 }}>
                                                                Juros e Multa
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body2" sx={{ color: '#EF4444', fontWeight: 600 }}>
                                                                + {Masks.money(String(totalInterest))}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                                {totalDiscount > 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={2}>
                                                            <Typography variant="body2" sx={{ color: '#22C55E', fontWeight: 500 }}>
                                                                Desconto
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body2" sx={{ color: '#22C55E', fontWeight: 600 }}>
                                                                - {Masks.money(String(totalDiscount))}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                                {totalPaid > 0 && (
                                                    <TableRow sx={{ bgcolor: '#F0FDF4' }}>
                                                        <TableCell colSpan={2}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#22C55E' }}>
                                                                Total Pago
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#22C55E' }}>
                                                                {Masks.money(String(totalPaid))}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Card>
                            </Grid>

                            {/* Right column - Actions & Info */}
                            <Grid item xs={12} md={4}>
                                {/* Actions Card */}
                                <Card
                                    sx={{
                                        border: '1px solid #E2E8F0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        borderRadius: 2,
                                        mb: 2,
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#94A3B8',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                fontSize: '0.7rem',
                                                letterSpacing: '0.05em',
                                                mb: 2,
                                            }}
                                        >
                                            Ações
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {invoice?.payment_page && (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        startIcon={<OpenInNew />}
                                                        href={invoice.payment_page}
                                                        target="_blank"
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            borderColor: '#E2E8F0',
                                                            color: '#3B82F6',
                                                            justifyContent: 'flex-start',
                                                            '&:hover': { borderColor: '#3B82F6', bgcolor: '#EFF6FF' },
                                                        }}
                                                    >
                                                        Página de Pagamento
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        startIcon={<ContentCopy />}
                                                        onClick={copyPaymentLink}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            borderColor: '#E2E8F0',
                                                            color: '#64748B',
                                                            justifyContent: 'flex-start',
                                                            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                                                        }}
                                                    >
                                                        Copiar Link
                                                    </Button>
                                                </>
                                            )}

                                            {invoice?.email_message?.id && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#F1F5F9', borderRadius: 1.5 }}>
                                                    <Email sx={{ fontSize: 18, color: '#3B82F6' }} />
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                                            E-mail enviado
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                            {invoice.email_message.sent_at
                                                                ? moment(invoice.email_message.sent_at).format('DD/MM/YYYY HH:mm')
                                                                : 'Aguardando envio'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            {invoice?.whatsapp_message?.id && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#F0FDF4', borderRadius: 1.5 }}>
                                                    <WhatsApp sx={{ fontSize: 18, color: '#22C55E' }} />
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                                            WhatsApp enviado
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                            {invoice.whatsapp_message.sent_at
                                                                ? moment(invoice.whatsapp_message.sent_at).format('DD/MM/YYYY HH:mm')
                                                                : 'Aguardando envio'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>

                                {/* Summary Card */}
                                <Card
                                    sx={{
                                        border: '1px solid #E2E8F0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        borderRadius: 2,
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#94A3B8',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                fontSize: '0.7rem',
                                                letterSpacing: '0.05em',
                                                mb: 2,
                                            }}
                                        >
                                            Resumo Financeiro
                                        </Typography>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                                                Valor Original
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 600 }}>
                                                {Masks.money(String(totalValue))}
                                            </Typography>
                                        </Box>

                                        {totalInterest > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="body2" sx={{ color: '#EF4444' }}>
                                                    Juros e Multa
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#EF4444', fontWeight: 600 }}>
                                                    + {Masks.money(String(totalInterest))}
                                                </Typography>
                                            </Box>
                                        )}

                                        {totalDiscount > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="body2" sx={{ color: '#22C55E' }}>
                                                    Desconto
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#22C55E', fontWeight: 600 }}>
                                                    - {Masks.money(String(totalDiscount))}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Divider sx={{ my: 1.5 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                {totalPaid > 0 ? 'Total Pago' : 'Total'}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: totalPaid > 0 ? '#22C55E' : '#1E293B',
                                                }}
                                            >
                                                {Masks.money(String(totalPaid > 0 ? totalPaid : totalValue))}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    ) : (
                        <Card sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                                Recebimento não encontrado
                            </Typography>
                        </Card>
                    )}
                </Container>
            </Box>
        </>
    )
}

export default ReceivablesDetail
